import { LightningElement, api, wire } from "lwc";
import { logger } from "@devforce/trailhead-client-logger";
import { createConfig } from "common/contentCollection";
import { EffectAdapter } from "common/effectAdapter";
import { QueryAdapter } from "common/graphqlAdapter";
import { LabelsAdapter } from "common/labelsAdapter";
import { memoizeDependencies } from "common/memoize";
import { trackUserActivity, trackUserActivityLearning } from "common/track";
import { showToast } from "tds/toaster";

var data$9 = {
  lazyLoadError: "Es gab ein Problem",
  ariaTotalResultsLoaded: "%{totalResults} Trailhead-Ergebnisse geladen",
  ariaNewResultsLoaded: "%{newResults} weitere Ergebnisse geladen",
};
data$9.lazyLoadError;
data$9.ariaTotalResultsLoaded;
data$9.ariaNewResultsLoaded;

var data$8 = {
  lazyLoadError: "There was a problem",
  ariaTotalResultsLoaded: "%{totalResults} Trailhead results loaded",
  ariaNewResultsLoaded: "%{newResults} more results loaded",
};
data$8.lazyLoadError;
data$8.ariaTotalResultsLoaded;
data$8.ariaNewResultsLoaded;

var data$7 = {
  lazyLoadError: "Se produjo un problema.",
  ariaTotalResultsLoaded:
    "%{totalResults} Se han cargado los resultados de Trailhead",
  ariaNewResultsLoaded: "%{newResults} resultados más cargados",
};
data$7.lazyLoadError;
data$7.ariaTotalResultsLoaded;
data$7.ariaNewResultsLoaded;

var data$6 = {
  lazyLoadError: "Se produjo un problema.",
  ariaTotalResultsLoaded: "%{totalResults} resultados de Trailhead cargados",
  ariaNewResultsLoaded: "%{newResults} resultados más cargados",
};
data$6.lazyLoadError;
data$6.ariaTotalResultsLoaded;
data$6.ariaNewResultsLoaded;

var data$5 = {
  lazyLoadError: "Un problème est survenu",
  ariaTotalResultsLoaded: "%{totalResults} résultats Trailhead chargés",
  ariaNewResultsLoaded: "%{newResults} résultats en plus chargés",
};
data$5.lazyLoadError;
data$5.ariaTotalResultsLoaded;
data$5.ariaNewResultsLoaded;

var data$4 = {
  lazyLoadError: "Si è verificato un problema",
  ariaTotalResultsLoaded: "%{totalResults} risultati di Trailhead caricati",
  ariaNewResultsLoaded: "Ulteriori risultati caricati: %{newResults}",
};
data$4.lazyLoadError;
data$4.ariaTotalResultsLoaded;
data$4.ariaNewResultsLoaded;

var data$3 = {
  lazyLoadError: "問題が発生しました",
  ariaTotalResultsLoaded:
    "%{totalResults} 件の Trailhead の結果が読み込まれました",
  ariaNewResultsLoaded: "さらに %{newResults} 件の結果が読み込まれました",
};
data$3.lazyLoadError;
data$3.ariaTotalResultsLoaded;
data$3.ariaNewResultsLoaded;

var data$2 = {
  lazyLoadError: "문제가 발생했습니다",
  ariaTotalResultsLoaded: "%{totalResults}개의 Trailhead 결과 로드됨",
  ariaNewResultsLoaded: "%{newResults}개의 더 많은 결과 로드됨",
};
data$2.lazyLoadError;
data$2.ariaTotalResultsLoaded;
data$2.ariaNewResultsLoaded;

var data$1 = {
  lazyLoadError: "Houve um problema",
  ariaTotalResultsLoaded: "%{totalResults} resultados do Trailhead carregados",
  ariaNewResultsLoaded: "Mais %{newResults} resultados carregados",
};
data$1.lazyLoadError;
data$1.ariaTotalResultsLoaded;
data$1.ariaNewResultsLoaded;

var data = {
  lazyLoadError: "出现问题",
  ariaTotalResultsLoaded: "已加载 %{totalResults} 个 Trailhead 结果",
  ariaNewResultsLoaded: "已加载另外 %{newResults} 个结果",
};
data.lazyLoadError;
data.ariaTotalResultsLoaded;
data.ariaNewResultsLoaded;

var locales = /*#__PURE__*/ Object.freeze({
  __proto__: null,
  de: data$9,
  en: data$8,
  es: data$7,
  esMX: data$6,
  fr: data$5,
  it: data$4,
  ja: data$3,
  ko: data$2,
  ptBR: data$1,
  zhCN: data,
});

class contentCollection extends LightningElement {
  @api config = null;
  @api query = null;
  @api queryMode = null;
  @api pageLocation = null;
  contentActionsContructor = null;
  hasMore = false;
  items = null;
  queryError = false;
  queryLoading = true;
  queryOptionsCache = undefined;
  queryRefreshKey = 0;
  // TODO: investigate a different way to achieve this functionality
  eventType = {
    cta: false,
  };
  // Values that we don't want to trigger an LWC update
  queryMeta = {
    state: undefined,
  };
  hasLoaded = false;
  totalCount = 0;
  lastCollectionItem = undefined;
  _newlyLoadedCount = 0;
  get newlyLoadedCount() {
    return this._newlyLoadedCount;
  }
  renderedCallback() {
    if (this.hasLoaded) return;
    if (this.state === "loaded") {
      this.manageFocus();
    }
    if (!this.isNewRecommendationQuery()) return;
    if (this.state === "loaded" && this.items?.length) {
      const contentUids = this.items?.map((i) => i.summary.id);
      const recommendationAction = this.items[0].action;
      if (
        recommendationAction.eventData?.name &&
        recommendationAction.eventData?.id
      ) {
        const { name: recommendationProvider, id: recommendationId } =
          recommendationAction.eventData;
        trackUserActivity(this, "recommendation_items_viewed", {
          contentUids,
          recommendationId,
          recommendationProvider,
        });
        this.hasLoaded = true;
      }
    }
  }
  configFinalMemoized = memoizeDependencies(
    () => {
      return createConfigFinal(this.config, {
        hasMore: this.hasMore,
        items: this.items,
        queryMode: this.queryMode,
        state: this.state,
      });
    },
    () => [this.config, this.hasMore, this.items, this.queryMode, this.state]
  );
  get configFinal() {
    return this.configFinalMemoized();
  }
  get loadingConfig() {
    return {
      ...this.configFinal.state,
      state: this.state,
    };
  }
  get queryOptions() {
    if (!this.query) return undefined;
    let metaState = this.eventType.cta ? this.queryMeta.state : undefined;
    try {
      return (
        this.query?.getQueryOptions?.(metaState) ??
        // @ts-ignore (depreacted, backwards-compatibility)
        this.query.queryOptions
      );
    } catch (e) {
      this.queryError = true;
      logger.error(e);
      return undefined;
    }
  }
  get queryUseFetch() {
    return this.query?.useFetch === true;
  }
  get showLoadingEmptyErrorState() {
    return !(this.queryMode && this.items && this.items.length > 0);
  }
  get state() {
    if (this.queryError) return "error";
    if (this.queryLoading) return "loading";
    if (this.items && this.items.length) return "loaded";
    return "empty";
  }
  manageFocus() {
    if (this.lastCollectionItem && this.lastCollectionItem.nextSibling) {
      const nextElement = this.lastCollectionItem.nextSibling;
      this.lastCollectionItem = undefined;
      nextElement.setAttribute("tabindex", "-1");
      nextElement.focus();
    }
  }
  setLastCollectionItemElement() {
    this.lastCollectionItem = Array.from(
      this.template.querySelectorAll('[data-name="content-collection-item"]')
    ).pop();
  }
  handleCta(e) {
    this.setLastCollectionItemElement();
    this.eventType.cta = true;
    if (this.queryMode) this.queryRefreshKey++;
    trackUserActivity(this, "cta_clicked", {
      ctaType: "primary",
      label: e.detail.ctaLabel ?? "",
      pageLocation: this.pageLocation ?? "th-content-collection",
    });
    this.dispatchEvent(
      new CustomEvent(e.type, {
        detail: e.detail,
      })
    );
  }
  handleNavigate(e) {
    const item = this.items?.find((i) => i.summary.id === e.detail.id);
    if (item && item.action && item.action.learning) {
      trackUserActivityLearning(this, item.action.learning, {
        pageLocation: this.pageLocation ?? "th-content-collection",
      });
      if (this.isRecommendation()) {
        this.trackRecommendationClicked(item);
      }
    } else {
      this.trackItemNavigation(e.detail);
    }
    this.dispatchEvent(
      new CustomEvent(e.type, {
        detail: e.detail,
      })
    );
  }
  trackItemNavigation(summary) {
    trackUserActivity(this, "link_clicked", {
      label: summary.title ?? summary.id,
      href: summary.path,
      pageLocation: this.pageLocation ?? "th-content-collection",
      pathName: `${window.location.pathname}${window.location.search}`,
    });
  }

  // TODO: this should be handled in recommendation component by listening to an event
  // or passing a click handling function
  trackRecommendationClicked(item) {
    if (item.action?.eventData?.name && item.action.eventData?.id) {
      const {
        learning: { name: apiName, type },
        eventData: { name, id },
      } = item.action;
      let payload;
      if (!this.isNewRecommendationQuery()) {
        payload = {
          recommenderName: name,
          recommendationSetId: id,
        };
      } else {
        payload = {
          recommendationProvider: name,
          recommendationId: id,
        };
      }
      payload = {
        ...payload,
        type,
        apiName,
        contentUid: item.summary.id,
      };
      trackUserActivity(this, "recommendation_clicked", payload);
    }
  }

  // TODO: this should be handled in recommendation component by listening to an event
  // or passing a click handling function
  isRecommendation() {
    if (this.queryOptions?.query.includes("getUserRecommendations")) {
      return true;
    }
    if (this.isNewRecommendationQuery()) {
      return true;
    }
  }

  // TODO: this should be handled in recommendation component by listening to an event
  // or passing a click handling function
  isNewRecommendationQuery() {
    if (this.queryOptions?.query.includes("getUserLearningRecommendations")) {
      return true;
    }
  }

  // TODO: this should be handled in recommendation component by listening to an event
  // or passing a click handling function
  handleDynamicCtaClick(e) {
    trackUserActivity(this, "cta_clicked", {
      ctaType: "dynamic",
      ctaHref: e.detail.ctaHref ?? "",
      label: e.detail.ctaLabel ?? "",
      pageLocation: this.pageLocation ?? "th-content-collection",
    });
    this.dispatchEvent(
      new CustomEvent(e.type, {
        detail: e.detail,
        bubbles: true,
      })
    );
  }
  handleRefresh(e) {
    this.queryRefreshKey++;
    this.dispatchEvent(
      new CustomEvent(e.type, {
        detail: e.detail,
      })
    );
  }
  @wire(LabelsAdapter, {
    locales,
    options: "$labelOptions",
  })
  labels = {};
  get labelOptions() {
    return {
      ariaTotalResultsLoaded: {
        totalResults: this.totalCount,
      },
      ariaNewResultsLoaded: {
        newResults: this._newlyLoadedCount,
      },
    };
  }
  @wire(EffectAdapter, {
    options: "$queryOptions",
    refreshKey: "$queryRefreshKey",
    useFetch: "$queryUseFetch",
  })
  onBeforeQuery() {
    this.queryError = false;
    this.queryLoading = true;
  }
  @wire(QueryAdapter, {
    options: "$queryOptions",
    refreshKey: "$queryRefreshKey",
    useFetch: "$queryUseFetch",
  })
  onResult(result) {
    try {
      let { items, query, queryMode } = this;
      if (!query) throw new Error("NO_QUERY");
      let nextItems = query.getCollectionItems(result);
      if (query.getTotalItemCount) {
        this.totalCount = query.getTotalItemCount(result);
        this.dispatchEvent(
          new CustomEvent("count", {
            detail: this.totalCount,
          })
        );
      }

      // Track newly loaded items for accessibility announcements
      const isAppendMode =
        queryMode === "append" && items && this.eventType.cta;
      if (isAppendMode && items) {
        this._newlyLoadedCount = nextItems.length;
        this.items = mergeItems(items, nextItems);
      } else {
        this._newlyLoadedCount = 0;
        this.items = nextItems;
      }
      this.hasMore = query.hasMore?.(result);
      this.queryMeta.state = query.getState?.(result);
    } catch (e) {
      logger.error(e);
      // If items have already been loaded, show a toast
      if (this.queryMode && this.items) {
        showToast({
          message: this.labels.lazyLoadError,
          variant: "error",
        });
      } else {
        this.queryError = true;
      }
    } finally {
      this.queryLoading = false;
      this.eventType.cta = false;
    }
  }
  @wire(EffectAdapter, {
    config: "$config",
  })
  async onConfigChange() {
    if (this.contentActionsContructor) return;
    if (this.config?.item?.hideAction) return;
    import("th/contentActions")
      .then((m) => {
        this.contentActionsContructor = m.default;
      })
      .catch((e) => logger.error(e));
  }
  @wire(EffectAdapter, {
    items: "$items",
    error: "$queryError",
  })
  onStateChange() {
    let { state } = this;
    let event = new CustomEvent("statechange", {
      detail: {
        state,
        items: this.items?.map((item) => item.summary),
      },
    });
    this.dispatchEvent(event);
  }
  @wire(EffectAdapter, {
    query: "$query",
  })
  onQuery() {
    let prev = this.queryOptionsCache;
    let next = this.queryOptions;
    this.queryOptionsCache = next;
    if (!prev || !next) return;
    let { query: prevQuery, ...prevOptions } = prev;
    let { query: nextQuery, ...nextOptions } = next;
    if (
      prevQuery === nextQuery &&
      JSON.stringify(prevOptions) === JSON.stringify(nextOptions)
    ) {
      return;
    }
    this.queryRefreshKey++;
    const { tab: prevTab, filter: prevFilters } = prev.variables ?? {};
    const { tab: nextTab, filter: nextFilters } = next.variables ?? {};
    if (
      (prevTab && nextTab && prevTab !== nextTab) ||
      (prevFilters && nextFilters && prevFilters !== nextFilters)
    ) {
      this.items = null;
    }
  }
}
function mergeItems(prev, next) {
  return prev
    .filter((p) => !next.find((n) => n.summary.id === p.summary.id))
    .concat(next);
}
function createConfigFinal(initialConfig, context) {
  let config = createConfig(initialConfig);
  if (config.card) {
    let showCta =
      config.card.ctaAlways === true ||
      (context.state === "loaded" && context.hasMore) ||
      (context.state === "loading" && context.hasMore && context.queryMode);
    if (!showCta) {
      delete config.card.ctaLabel;
      delete config.card.ctaHref;
    }
    if (context.queryMode) {
      delete config.card.ctaHref;
    }
    if (context.queryMode && context.items && context.state === "loading") {
      config.card.ctaDisabled = true;
      config.card.ctaSpinner = true;
    }
    // Prevent the loading state from displaying in a grid
    if (
      context.state !== "loaded" &&
      !(context.state === "loading" && config.state?.loading?.preserveLayout)
    ) {
      config.card.layout = "stack";
    }
  }
  return config;
}

export default contentCollection;
export { createConfigFinal, mergeItems };
